import { Component, OnDestroy, OnInit, Signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { select, Store } from '@ngrx/store';

import { combineLatest, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import {
  PackageUser,
  PackageUserRole,
  PackageUsersActions,
  PackageUsersSelectors,
} from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { getPathForUrl } from 'src/app/features/shared';
import { CheckInActions, CheckInSelectors } from 'src/app/features/check-in/store';

import { ParticipantListViewModel } from './participant-list-view.model';
import {
  getEsignParticipantStepLabel,
  getPackageUserFullName,
  isCheckInComplete,
} from 'src/app/features/package-users/util';

@Component({
    selector: 'app-participant-list',
    templateUrl: './participant-list.component.html',
    styleUrls: ['./participant-list.component.scss'],
    standalone: false
})
export class ParticipantListComponent implements OnInit, OnDestroy {
  isSigningAgent: Signal<boolean> = this.store.selectSignal(
    PackageUsersSelectors.getIsSigningAgent
  );
  isRemoteSigning: Signal<boolean> = this.store.selectSignal(PackagesSelectors.isRONOrKRON);
  usersViewedWaitingRoom: Signal<string[]> = this.store.selectSignal(
    CheckInSelectors.getUsersViewedWaitingRoom
  );

  packageUsers$: Observable<ParticipantListViewModel[]>;

  signingAgentRoleCode: string = PackageUserRole.SIGNINGAGENT;

  private readonly onDestroyNotifier$ = new Subject();
  private readonly MOBILE_MAX_WIDTH = 575;

  constructor(private readonly route: ActivatedRoute, private readonly store: Store) {}

  get isMobile() {
    return window.innerWidth <= this.MOBILE_MAX_WIDTH;
  }

  ngOnInit(): void {
    const routePath = getPathForUrl(this.route.snapshot.url.toString());

    this.store.dispatch(
      PackageUsersActions.FetchPackageUsers({ payload: { ignoreIfLoaded: true } })
    );

    if (this.isRemoteSigning()) {
      this.store.dispatch(CheckInActions.FetchWaitingRoomViewedByParticipants());
    }

    this.packageUsers$ = combineLatest([
      this.store.pipe(select(PackageUsersSelectors.getPackageUsers)),
      this.store.pipe(select(PackageUsersSelectors.getNonSigningAgents)),
    ]).pipe(
      map(([packageUsers, nonSigningAgents]) => {
        const isSAForRonKronInWelcomePage =
          this.isSigningAgent() && this.isRemoteSigning() && routePath.includes('welcome');
        return isSAForRonKronInWelcomePage ? nonSigningAgents : packageUsers;
      }),
      map((packageUsers) =>
        packageUsers.map((user) => {
          return {
            name: getPackageUserFullName(user, ''),
            role: user.userRoleCode,
            isLoaded: !!user.checkInStatus,
            progress: Math.max(4, user.checkInStatus?.percentComplete),
            secondsSinceLastAction: this.resolveSecondsSinceLastAction(user),
            status: this.resolveStatus(user),
          };
        })
      )
    );
  }

  isReady(packageUser: PackageUser) {
    if (this.isRemoteSigning()) {
      return (
        isCheckInComplete(packageUser.checkInStatus) &&
        this.usersViewedWaitingRoom().includes(packageUser.packageUserGuid)
      );
    }

    return isCheckInComplete(packageUser.checkInStatus);
  }

  private resolveStatus(packageUser: PackageUser): string {
    if (this.isReady(packageUser)) {
      return 'Ready';
    }

    if (packageUser.checkInStatus?.esignStep) {
      return getEsignParticipantStepLabel(
        packageUser.checkInStatus.esignStep,
        packageUser.checkInStatus.esignStepStatus
      );
    }

    return packageUser.checkInStatus?.currentStatus;
  }

  private resolveSecondsSinceLastAction(packageUser: PackageUser): number {
    return packageUser.checkInStatus?.secondsSinceLastAction;
  }

  ngOnDestroy(): void {
    this.onDestroyNotifier$.next(undefined);
    this.onDestroyNotifier$.complete();
  }
}
